import React, { useState } from 'react';
import { IntranetTask, InquiryType } from '../../types';
import './TaskInquiryModal.css';

interface TaskInquiryModalProps {
  task: IntranetTask;
  toEmployeeId: string;
  toEmployeeName: string;
  fromEmployeeId: string;
  fromEmployeeName: string;
  onSubmit: (data: {
    toEmployeeId: string;
    toEmployeeName: string;
    intranetTaskId: string;
    taskDescription: string;
    taskProject: string;
    taskType: string;
    taskTimeSeconds: number;
    inquiryType: InquiryType;
    message: string;
  }) => Promise<void>;
  onClose: () => void;
}

const formatTaskTime = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  if (hours > 0) {
    return `${hours}h ${mins}m`;
  }
  return `${mins}m`;
};

const QUICK_QUESTIONS = [
  { label: 'Why did this take so long?', type: 'question' as InquiryType },
  { label: 'Were there any blockers?', type: 'question' as InquiryType },
  { label: 'Any issues I should know about?', type: 'concern' as InquiryType },
  { label: 'Can you provide more details?', type: 'followup' as InquiryType },
  { label: 'Is this task complete?', type: 'followup' as InquiryType },
];

const TaskInquiryModal: React.FC<TaskInquiryModalProps> = ({
  task,
  toEmployeeId,
  toEmployeeName,
  fromEmployeeId,
  fromEmployeeName,
  onSubmit,
  onClose
}) => {
  const [inquiryType, setInquiryType] = useState<InquiryType>('question');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleQuickQuestion = (question: string, type: InquiryType) => {
    setMessage(question);
    setInquiryType(type);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!message.trim()) {
      setError('Please enter a message');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await onSubmit({
        toEmployeeId,
        toEmployeeName,
        intranetTaskId: task.id,
        taskDescription: task.description,
        taskProject: task.projectName || task.company || 'Internal',
        taskType: task.type,
        taskTimeSeconds: task.timeTotal,
        inquiryType,
        message: message.trim()
      });
      onClose();
    } catch (err) {
      setError('Failed to send inquiry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="task-inquiry-modal-overlay" onClick={onClose}>
      <div className="task-inquiry-modal" onClick={e => e.stopPropagation()}>
        <div className="task-inquiry-header">
          <h3>Ask About Task</h3>
          <button className="task-inquiry-close" onClick={onClose}>×</button>
        </div>

        <div className="task-inquiry-task-info">
          <div className="task-info-row">
            <span className="task-info-label">Developer:</span>
            <span className="task-info-value">{toEmployeeName}</span>
          </div>
          <div className="task-info-row">
            <span className="task-info-label">Task:</span>
            <span className="task-info-value task-description">{task.description || 'No description'}</span>
          </div>
          <div className="task-info-row">
            <span className="task-info-label">Project:</span>
            <span className="task-info-value">{task.projectName || task.company || 'Internal'}</span>
          </div>
          <div className="task-info-row">
            <span className="task-info-label">Time Spent:</span>
            <span className="task-info-value">{formatTaskTime(task.timeTotal)}</span>
          </div>
        </div>

        <div className="task-inquiry-quick-questions">
          <span className="quick-questions-label">Quick questions:</span>
          <div className="quick-questions-list">
            {QUICK_QUESTIONS.map((q, idx) => (
              <button
                key={idx}
                type="button"
                className={`quick-question-btn ${message === q.label ? 'selected' : ''}`}
                onClick={() => handleQuickQuestion(q.label, q.type)}
              >
                {q.label}
              </button>
            ))}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="task-inquiry-form">
          <div className="form-group">
            <label>Type</label>
            <select
              value={inquiryType}
              onChange={e => setInquiryType(e.target.value as InquiryType)}
            >
              <option value="question">Question</option>
              <option value="concern">Concern</option>
              <option value="followup">Follow-up</option>
            </select>
          </div>

          <div className="form-group">
            <label>Message</label>
            <textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder="Type your question or message..."
              rows={4}
              autoFocus
            />
          </div>

          {error && <div className="task-inquiry-error">{error}</div>}

          <div className="task-inquiry-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting || !message.trim()}
            >
              {submitting ? 'Sending...' : 'Send to Developer'}
            </button>
          </div>
        </form>

        <p className="task-inquiry-note">
          This will appear as a notification in eDoss for {toEmployeeName.split(' ')[0]}.
        </p>
      </div>
    </div>
  );
};

export default TaskInquiryModal;

